import React from 'react';
import { StashCategory } from '@stash/shared-types';
import { Badge as _Badge } from '@/components/ui/badge';
import { deriveColor } from '@/utils/category-color';

// badge.jsx not yet typed; cast to avoid inferred `render: any` required prop
const Badge = _Badge as React.ComponentType<{
  variant?: string;
  className?: string;
  children?: React.ReactNode;
}>;

interface CategoryGridProps {
  categories?: StashCategory[];
  /** Vendor count per category recordID; missing keys render as 0. */
  counts?: Record<string, number>;
  /** The currently selected category recordID, highlighted in the grid. */
  selectedCategoryId?: string | null;
  onCategoryClick?: (category: StashCategory) => void;
}

interface CategoryCardProps {
  category: StashCategory;
  count: number;
  selected: boolean;
  // non-optional union so undefined can be forwarded from CategoryGrid's optional prop
  onClick: ((category: StashCategory) => void) | undefined;
}

interface CategoryGridSkeletonProps {
  count?: number;
}

export function CategoryGrid({ categories = [], counts = {}, selectedCategoryId = null, onCategoryClick }: CategoryGridProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 lg:gap-6 px-4 md:px-6 lg:px-8 mt-4 pb-24">
      {categories.map((category) => (
        <CategoryCard
          key={category.recordID}
          category={category}
          count={counts[category.recordID] ?? 0}
          selected={category.recordID === selectedCategoryId}
          onClick={onCategoryClick}
        />
      ))}
    </div>
  );
}

function CategoryCard({ category, count, selected, onClick }: CategoryCardProps) {
  return (
    <button
      onClick={() => onClick?.(category)}
      className={`group relative flex flex-col overflow-hidden rounded-xl bg-card shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 text-left ${
        selected ? 'ring-2 ring-[var(--stash-blue)]' : ''
      }`}
    >
      {/* Image area */}
      <div className="relative aspect-[4/3] overflow-hidden">
        {category.imageUrl ? (
          <img
            src={category.imageUrl}
            alt={category.name}
            className="w-full h-full object-cover saturate-[0.6] group-hover:saturate-100 group-hover:scale-105 transition-all duration-500"
            loading="lazy"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${deriveColor(category.name)}`} />
        )}

        {/* Card count badge */}
        <Badge
          variant="secondary"
          className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm text-white border-0 text-[11px] font-semibold px-2 py-0.5"
        >
          {count} Cards
        </Badge>
      </div>

      {/* Name */}
      <div className="px-3 py-2.5">
        <h3 className="text-sm font-semibold leading-tight truncate group-hover:text-[var(--stash-blue)] transition-colors">
          {category.name}
        </h3>
      </div>
    </button>
  );
}

export function CategoryGridSkeleton({ count = 9 }: CategoryGridSkeletonProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 lg:gap-6 px-4 md:px-6 lg:px-8 mt-4 pb-24">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col overflow-hidden rounded-xl bg-card shadow-sm animate-pulse"
        >
          <div className="aspect-[4/3] bg-muted" />
          <div className="px-3 py-2.5">
            <div className="h-4 bg-muted rounded-md w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}
